import React from 'react';
import { Zap } from 'lucide-react';

import { useFeatures } from '../../../../context/FeatureContext';
import SettingsCard from '../SettingsCard';
import ScheduledCard from '../ScheduledCard';
import RulesPage from '../../Banking/RulesPage';
import AutoPostSettingsSection from '../../Banking/AutoPostSettingsSection';
import AutomationPolicySettings from '../../Banking/reconciliation/AutomationPolicySettings';
import ConsistencyFindingsSection from '../../Banking/ConsistencyFindingsSection';

/**
 * Inställningar → Automatisering: what may happen on Bankhändelser without
 * the user. Four cards, read top to bottom by someone who has never seen
 * the engine (owner 2026-10-01: novice-first):
 *
 *   1. Regler — the user's own rules (RulesPage embedded; the "Skapa
 *      regel?" CTA deep-links here with its prefill).
 *   2. Automatisk bokföring — Nödstopp first (the one brake, stops the
 *      engine and Mats), then one table per type of transaction with how
 *      far it has come toward being allowed to book by itself, the switch
 *      and the beloppstak; then Kvalitetskontroll. Shown with auto_post.
 *   3. Mats — what the reconciliation agent may carry out without approval
 *      (owner-only). Shows whenever the company has a policy, independent
 *      of auto_post.
 *
 *   4. Schemalagt — a read-only list of what LedgerLoop runs on a schedule
 *      for the company (depreciation today), each row linking to the
 *      settings page that owns it. Hidden while nothing is scheduled.
 *
 * Moved out 2026-10-01: AI-historik lives under Import & export (it learns
 * from imported history), Dela anonym statistik under Prenumeration &
 * krediter (a company-wide choice). Everything here is off until switched on.
 */

export default function AutomationSection({ companyId, canEditAgentPolicy = true }) {
    const { features } = useFeatures();
    const hasAutoPost = Boolean(features?.auto_post);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 1 ── Regler (embedded manager, router-state prefill intact) */}
            <RulesPage embedded />

            {/* 2 ── The engine: Nödstopp, one table of transaction types with
                their readiness and switch, Kvalitetskontroll. */}
            {hasAutoPost && (
                <SettingsCard
                    icon={Zap}
                    title="Automatisk bokföring"
                    subtitle="Låt LedgerLoop bokföra vissa bankhändelser utan att du granskar dem först. Allt är avstängt tills du slår på det."
                >
                    <div className="automation-group">
                        <AutoPostSettingsSection companyId={companyId} />
                        <ConsistencyFindingsSection companyId={companyId} />
                    </div>
                </SettingsCard>
            )}

            {/* 3 ── Mats. Hides itself when the company has no policy (403/404). */}
            <AutomationPolicySettings companyId={companyId} canEdit={canEditAgentPolicy} />

            {/* 4 ── Schemalagt. Hides itself when nothing is scheduled. */}
            <ScheduledCard companyId={companyId} />
        </div>
    );
}
